import React from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@tickchak/react-share/app';
import type {AiAgentPendingFeedback} from '../../../../redux/aiAgent/aiAgent.type';
import {useAiAgentFeedback} from '../../hooks/useAiAgentFeedback';
import AiAgentFeedbackStars from './AiAgentFeedbackStars';
import * as css from './AiAgentFeedback.css';

interface AiAgentFeedbackProps {
    feedback: AiAgentPendingFeedback;
}

/** Asked once the record exists: how the chat went, in words and as a 1-5 score. */
function AiAgentFeedback({feedback}: AiAgentFeedbackProps) {
    const {t} = useTranslation('aiAgent');
    const {rating, setRating, text, setText, loading, sent, submit, skip} = useAiAgentFeedback(feedback);

    if(sent) {
        return <css.thanks data-cy="ai-agent-feedback-thanks">{t('feedbackThanks')}</css.thanks>;
    }

    return (
        <css.card data-cy="ai-agent-feedback">
            <css.title>{t('feedbackTitle')}</css.title>
            <css.question>{t('feedbackRatingQuestion')}</css.question>
            <AiAgentFeedbackStars value={rating} disabled={loading} onChange={setRating} />
            <css.textField
                value={text}
                placeholder={t('feedbackPlaceholder')}
                disabled={loading}
                onChange={event => setText(event.target.value)}
                data-cy="ai-agent-feedback-text"
            />
            <css.actions>
                <Button
                    variant="subtle"
                    size="category"
                    disabled={loading}
                    onClick={skip}
                    data-cy="ai-agent-feedback-skip"
                >
                    {t('feedbackSkip')}
                </Button>
                <Button
                    variant="brand"
                    size="category"
                    disabled={loading || !rating}
                    onClick={() => void submit()}
                    data-cy="ai-agent-feedback-send"
                >
                    {t('feedbackSend')}
                </Button>
            </css.actions>
        </css.card>
    );
}

export default AiAgentFeedback;
